import { CSSObject } from '@emotion/react'
import { KeyboardEvent, MouseEvent, ReactElement } from 'react'
import { RequireAtLeastOne } from 'type-fest'
import { Theme } from '@opallabs/gem'
import { Position } from '../../gem'
import type { WithMultipleStyleOverrides } from '../../gem'
import { Variants } from '../ButtonVariant/variants'

export const enum Size {
  Regular = 'regular',
  Small = 'small',
}

export enum ButtonIconPosition {
  leading = 'leading',
  trailing = 'trailing',
}

/**
 * The HTML `type` attribute.
 */
export enum TypeAttribute {
  button = 'button',
  submit = 'submit',
  Submit = submit,
  reset = 'reset',
}

type IntrinsicButtonProps = React.DetailedHTMLProps<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>

type BaseButtonProps = Readonly<
  Omit<IntrinsicButtonProps, 'onClick'> &
    WithMultipleStyleOverrides<'button' | 'icon' | 'menuIndicator' | 'text'> & {
      ariaControls?: string
      ariaExpanded?: boolean
      /**
       * Copied from `@types/react` `AriaAttributes`.
       * @see https://developer.mozilla.org/en-US/docs/Web/API/Element/ariaHasPopup
       */
      ariaHasPopup?:
        | boolean
        | 'false'
        | 'true'
        | 'menu'
        | 'listbox'
        | 'tree'
        | 'grid'
        | 'dialog'
      ariaLabel?: string
      ariaLabelledBy?: string
      ariaLive?: 'off' | 'assertive' | 'polite'
      ariaPressed?: boolean
      ariaSelected?: boolean
      childrenPosition?: Position
      className?: string
      disabled?: boolean
      displayMenuIndicator?: boolean
      hideButtonText?: boolean
      isLoading?: boolean
      /**
       * Provide a custom menu indicator, displayed if `displayMenuIndicator` is
       * `true`. `menuIndicator` should be `aria-hidden`. The menu indicator is
       * always rendered after `buttonText` and `icon`.
       *
       * @default gem-chevron-s
       */
      menuIndicator?: ReactElement
      name: string
      onClick?: (
        event: MouseEvent<HTMLButtonElement>,
        byKeypress: boolean
      ) => void
      onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
      /**
       * @deprecated Migrate to `styleOverrides.icon`.
       */
      overrideIconStyles?: CSSObject | Array<CSSObject | undefined>
      /**
       * @deprecated Migrate to `styleOverrides.button`.
       */
      overrideStyles?: CSSObject | Array<CSSObject | undefined>
      ref?: React.ComponentPropsWithRef<'button'>['ref']
      role?: string
      showAsActiveWhenPressed?: boolean
      size?: Size
      tabIndex?: number
      /** @deprecated Wrap component tree in a `<ThemeProvider>` from @opallabs/hooks instead */
      theme?: Theme
      /**
       * The HTML `type` attribute.
       */
      typeAttribute?: TypeAttribute
      /**
       * The base "style" of the Button from the Gem design system.
       *
       * @default "primary"
       */
      variant?: Variants
    }
>

type ButtonPropsTextOnly = {
  buttonText: string
  icon?: never
  iconPosition?: never
  menuIndicatorPosition?: never
}

type ButtonPropsIconOnly = {
  buttonText?: never
  icon: ReactElement
  iconPosition?: never
  iconOverrides?: CSSObject
  menuIndicatorPosition?: never
}

type ButtonPropsTextAndIcon = {
  buttonText: string
  iconPosition: ButtonIconPosition
  icon: ReactElement
  menuIndicatorPosition?: ButtonIconPosition
}

export type ButtonProps = RequireAtLeastOne<
  BaseButtonProps &
    (ButtonPropsTextAndIcon | ButtonPropsTextOnly | ButtonPropsIconOnly),
  | 'aria-label'
  | 'aria-labelledby'
  | 'ariaLabel'
  | 'ariaLabelledBy'
  | 'buttonText'
>

/** For use in Storybook, which can’t extract destructured defaults within `forwardRef()` */
export const propDefaults = {
  childrenPosition: Position.End,
  disabled: false,
  displayMenuIndicator: false,
  hideButtonText: false,
  showAsActiveWhenPressed: true,
  size: Size.Regular,
  tabIndex: 0,
  typeAttribute: TypeAttribute.button,
  variant: 'primary',
} as const satisfies Omit<ButtonProps, 'name' | 'onClick' | 'onKeyDown'>

export type StyleProps = Required<
  Pick<BaseButtonProps, 'displayMenuIndicator' | 'theme'>
>
